import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Alert, Spinner, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';

const EmailConfirmation = () => {
    const [status, setStatus] = useState('pending'); // 'pending', 'verifying', 'success', 'error'
    const [error, setError] = useState('');
    const [tempUser, setTempUser] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const tempUserRef = useRef(null);
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const { user, pgUser, updateEmailConfirmationSecure } = useAuth();

    // Get email from temp user or logged in user
    const userEmail = tempUser?.email || tempUserRef.current?.email || user?.email;

    // Handle OTP verification
    const handleVerifyOtp = async () => {
        if (otpCode.length !== 6) {
            setError('Please enter all 6 digits of your verification code');
            return;
        }

        if (!userEmail) {
            setError('No email found. Please try signing up again.');
            return;
        }

        setStatus('verifying');
        setError('');

        try {
            console.log('Verifying OTP for email:', userEmail);
            
            const { data, error: verifyError } = await supabase.auth.verifyOtp({
                email: userEmail,
                token: otpCode,
                type: 'signup'
            });

            if (verifyError) {
                console.error('OTP verification failed:', verifyError);
                setStatus('pending');
                if (verifyError.message.includes('expired')) {
                    setError('This code has expired. Please request a new one.');
                } else if (verifyError.message.includes('invalid')) {
                    setError('Invalid verification code. Please check and try again.');
                } else {
                    setError(verifyError.message || 'Verification failed. Please try again.');
                }
                return;
            }

            console.log('OTP verification successful:', data);

            // Update PostgreSQL email confirmation
            if (data?.user?.id) {
                try {
                    const result = await updateEmailConfirmationSecure(data.user.id);
                    console.log('Email confirmation update result:', result);
                    
                    setStatus('success');
                    sessionStorage.removeItem('tempSignupUser');
                    
                    // Redirect based on setup status
                    setTimeout(() => {
                        if (result?.user?.setup_complete) {
                            navigate('/meal-plan');
                        } else {
                            navigate('/setup');
                        }
                    }, 1500);
                } catch (pgError) {
                    console.error('Failed to update PG email confirmation:', pgError);
                    // Still redirect since Supabase verification succeeded
                    setStatus('success');
                    sessionStorage.removeItem('tempSignupUser');
                    setTimeout(() => navigate('/setup'), 1500);
                }
            }
        } catch (err) {
            console.error('Exception during OTP verification:', err);
            setStatus('pending');
            setError('An unexpected error occurred. Please try again.');
        }
    };

    // Handle resend OTP
    const handleResendCode = async () => {
        if (!userEmail) {
            setError('No email found. Please try signing up again.');
            return;
        }

        setIsResending(true);
        setError('');
        setResendSuccess(false);

        try {
            const { error: resendError } = await supabase.auth.resend({
                type: 'signup',
                email: userEmail
            });

            if (resendError) {
                console.error('Resend failed:', resendError);
                setError('Failed to resend code. Please try again.');
            } else {
                setResendSuccess(true);
                setOtpCode('');
                // Clear success message after 5 seconds
                setTimeout(() => setResendSuccess(false), 5000);
            }
        } catch (err) {
            console.error('Exception during resend:', err);
            setError('Failed to resend code. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    // Handle OTP input change - single input field
    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtpCode(value);
        setError('');
    };

    // Handle keyboard submit
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && otpCode.length === 6) {
            handleVerifyOtp();
        }
    };

    // Load temp user from session storage on mount
    useEffect(() => {
        console.log('EmailConfirmation: Component mounted');
        
        const storedTempUser = sessionStorage.getItem('tempSignupUser');
        if (storedTempUser) {
            try {
                const parsedTempUser = JSON.parse(storedTempUser);
                setTempUser(parsedTempUser);
                tempUserRef.current = parsedTempUser;
                console.log('Loaded temp user:', parsedTempUser.email);
            } catch (error) {
                console.error('Error parsing stored temp user:', error);
                sessionStorage.removeItem('tempSignupUser');
            }
        }
    }, []);

    // Redirect if already confirmed
    useEffect(() => {
        if (pgUser?.email_confirmed) {
            sessionStorage.removeItem('tempSignupUser');
            if (pgUser?.setup_complete) {
                navigate('/meal-plan');
            } else {
                navigate('/setup');
            }
        }
    }, [pgUser, navigate]);

    // Redirect to login if no user context
    useEffect(() => {
        const currentUser = user || tempUser || tempUserRef.current;
        if (!currentUser) {
            // Give a moment for temp user to load
            const timer = setTimeout(() => {
                const hasUser = user || tempUser || tempUserRef.current;
                if (!hasUser) {
                    console.log('No user found, redirecting to login');
                    navigate('/login');
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [user, tempUser, navigate]);

    const renderContent = () => {
        switch (status) {
            case 'pending':
                return (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="fas fa-envelope text-primary" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3>Verify Your Email</h3>
                        <p className="text-muted mb-4">
                            We've sent a 6-digit verification code to<br />
                            <strong>{userEmail || 'your email'}</strong>
                        </p>

                        {/* OTP Input */}
                        <Form.Group className="mb-4">
                            <Form.Label className="text-muted small">Enter verification code</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                value={otpCode}
                                onChange={handleOtpChange}
                                onKeyDown={handleKeyDown}
                                placeholder="000000"
                                className="text-center fs-3 py-3"
                                style={{ 
                                    letterSpacing: '0.5em', 
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold'
                                }}
                                autoFocus
                            />
                        </Form.Group>

                        {/* Error Message */}
                        {error && (
                            <Alert variant="danger" className="mb-3">
                                {error}
                            </Alert>
                        )}

                        {/* Success Message */}
                        {resendSuccess && (
                            <Alert variant="success" className="mb-3">
                                A new code has been sent to your email!
                            </Alert>
                        )}

                        {/* Verify Button */}
                        <Button 
                            variant="primary" 
                            size="lg" 
                            className="w-100 mb-3"
                            onClick={handleVerifyOtp}
                            disabled={otpCode.length !== 6}
                        >
                            Verify Email
                        </Button>

                        {/* Resend Link */}
                        <p className="text-muted small mb-0">
                            Didn't receive the code?{' '}
                            <Button 
                                variant="link" 
                                className="p-0 text-decoration-none"
                                onClick={handleResendCode}
                                disabled={isResending}
                            >
                                {isResending ? 'Sending...' : 'Resend code'}
                            </Button>
                        </p>

                        <p className="text-muted small mt-2">
                            Check your spam folder if you don't see it.
                        </p>
                    </div>
                );

            case 'verifying':
                return (
                    <div className="text-center">
                        <Spinner animation="border" role="status" className="mb-3">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <h3>Verifying...</h3>
                        <p>Please wait while we verify your code.</p>
                    </div>
                );
            
            case 'success':
                return (
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3>Email Verified!</h3>
                        <p>Your email has been successfully verified. Redirecting you to the next step...</p>
                    </div>
                );
            
            case 'error':
                return (
                    <div className="text-center">
                        <Alert variant="danger">
                            <h4>Verification Failed</h4>
                            <p>{error}</p>
                            <hr />
                            <p className="mb-0">
                                <a href="/login" className="alert-link">Return to login</a> or{' '}
                                <a href="/signup" className="alert-link">sign up again</a>
                            </p>
                        </Alert>
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <Container fluid className="py-5 px-3">
            <Row className="justify-content-center m-0">
                <Col md={6} lg={4}>
                    {renderContent()}
                </Col>
            </Row>
        </Container>
    );
};

export default EmailConfirmation;
